import type { HierarcicalObjectReference, ObjectId } from "@novorender/data-js-api";

import { estimateShrunkSize, IdArrayShrinker, pickBestShrink, type ShrunkIdArray } from "utils/idArrayShrinker";
import { registerMemoryStatsProvider } from "utils/memoryDiagnostics";

/**
 * Budget for the shrunk sets, in bytes.
 *
 * A byte budget rather than an entry count: a leaf's set is tens of bytes and a whole
 * file's is tens of kilobytes even shrunk, so a fixed count would either starve a dense
 * scene or admit far more than intended.
 */
const MAX_TOTAL_BYTES = 8 * 1024 * 1024;

/**
 * Below this many ids a set is kept as a plain `Uint32Array`: at that size the
 * shrink/expand overhead outweighs the bytes saved. Same threshold as the object DB's
 * descendants cache in `utils/search`.
 */
const SHRINK_MIN_IDS = 1024;

/**
 * Budget for the hot cache of expanded arrays, in total ids across entries (512k ids =
 * 2 MB as `Uint32Array`). An entry count doesn't work here: entries range from ~1k to
 * ~400k ids, so a fixed count either starves a dense scene or admits tens of MB.
 */
const HOT_CACHE_MAX_TOTAL_IDS = 512 * 1024;

/**
 * What a loader answers {@link DescendantCache.getOrLoad} with — the ids themselves.
 * Whatever form they arrived in is the loader's business: a served index answers in
 * portable roaring bytes and decodes them on the way here, the offline engine
 * materializes its own subtree and has nothing to decode.
 */
export interface LoadedSet {
    ids: Uint32Array;
    /**
     * Whether the set covers the whole subtree. A degraded read is short: usable for the
     * caller that asked, but not worth remembering, since an entry would hand that short
     * set to every later reader instead of letting a later read get it right.
     */
    complete: boolean;
}

/** A stored set, shrunk when that pays off. */
type CachedDescendants = Uint32Array | ShrunkIdArray;

/**
 * The read behind an in-flight entry, and the askers whose signals decide its fate.
 *
 * The cache owns the signal the loader runs under, rather than the first asker's: askers
 * join one read, so letting one of them cancel it would empty the answer for everyone
 * else still waiting. It is abandoned when the last asker gives up, and not before.
 */
interface SharedRead {
    controller: AbortController;
    /** Askers that have not aborted. An asker with no signal never decrements. */
    askers: number;
    /** Undoes the abort subscriptions, run once the read settles. */
    release: (() => void)[];
}

/**
 * One id's set, in one of two states.
 *
 * Pending while a read or a decode is in flight, so concurrent askers join it instead of
 * each making their own — the promise *is* the entry, as in `ObjectMetadataCache`. Once
 * it settles the promise is dropped and {@link ids} remains.
 */
interface Entry {
    pending?: Promise<Uint32Array>;
    shared?: SharedRead;
    ids?: CachedDescendants;
    /** Ids in the set, kept so diagnostics can report a shrunk entry's size. */
    idCount: number;
    /** Kept only so diagnostics can name the largest folders. */
    path: string;
}

/**
 * Subtree sets for one BIM index session — what backs `object.descendants` on this
 * backend.
 *
 * **Two tiers, and the expanded ids are the smaller one.** A resolved set is stored
 * shrunk (`utils/idArrayShrinker`, the same bitmap/RLE form the object DB's descendants
 * cache uses) and expanded on demand through a hot cache bounded at
 * {@link HOT_CACHE_MAX_TOTAL_IDS}. `treeHelpers` counts a folder's descendants against
 * the selection *synchronously* to render a collapsed checkbox, and that read is what
 * {@link defineLazyDescendants} serves — so the reference carries an accessor over this
 * cache rather than the array itself. A warm-up over thousands of folders therefore
 * costs a bounded amount of memory instead of one expanded `Uint32Array` per node,
 * pinned for as long as the tree holds the reference.
 *
 * **Ids, not the bitmap they may have arrived as.** A set is stored as the ids a loader
 * hands over: the shrunk form is comparable in size to the serialized bitmap, expands
 * in-process, and so can back a synchronous getter where a worker decode cannot. A loader
 * that decodes is therefore free to let the decoder take its buffer — nothing here
 * outlives that call.
 *
 * **Keyed by object id, not by reference.** The tree recreates
 * `HierarcicalObjectReference`s constantly while loading, so a reference-keyed cache
 * would forget a folder's set the moment its node was rebuilt and go back to the network
 * for it.
 *
 * One instance per backend, so the whole cache dies with the scene.
 */
export class DescendantCache {
    /** Iteration order is LRU order: entries are re-inserted on access. */
    private readonly sets = new Map<ObjectId, Entry>();
    /** Only counts entries that actually hold a set. */
    private storedBytes = 0;

    /** Expanded sets, LRU as above, bounded by {@link HOT_CACHE_MAX_TOTAL_IDS}. */
    private readonly hot = new Map<ObjectId, Uint32Array>();
    private hotIds = 0;

    /**
     * @param maxTotalBytes Budget for the stored sets. Defaults to {@link MAX_TOTAL_BYTES};
     * overridable so a test can drive eviction without allocating megabytes.
     */
    constructor(private readonly maxTotalBytes = MAX_TOTAL_BYTES) {
        trackForDiagnostics(this);
    }

    /**
     * The subtree ids for `object` — from a stored set, from a read in flight for the same
     * id, or by calling `load`.
     *
     * `load` is handed a signal of the cache's own, live for as long as any asker still
     * wants the answer (see {@link SharedRead}). It answers `undefined` for a read that
     * produced nothing, which is what an abandoned read looks like: the empty value goes
     * back to the callers and nothing is remembered, so a later ask retries rather than
     * inheriting the silence.
     *
     * @param signal This asker's signal. Its abort withdraws this asker only.
     */
    getOrLoad(
        object: HierarcicalObjectReference,
        signal: AbortSignal | undefined,
        load: (signal: AbortSignal) => Promise<LoadedSet | undefined>,
    ): Promise<Uint32Array> {
        const existing = this.sets.get(object.id);
        if (existing?.pending) {
            this.attend(existing.shared, signal);
            return existing.pending;
        }
        if (existing?.ids !== undefined) {
            // No decode and no promise: expanding a stored set is in-process work, which
            // is the point of keeping ids here rather than the bytes they arrived as.
            this.touch(object.id, existing);
            return Promise.resolve(this.expand(object.id, existing.ids));
        }
        const entry: Entry = {
            path: object.path,
            idCount: 0,
            shared: { controller: new AbortController(), askers: 0, release: [] },
        };
        this.attend(entry.shared, signal);
        return this.share(object.id, entry, this.loadThenKeep(object.id, entry, load));
    }

    /**
     * Ids for an id resolved in this session, or `undefined` — the synchronous read
     * behind {@link defineLazyDescendants}, and with it every synchronous consumer
     * (group-state counting in `treeHelpers`, preload-decision checks, ...).
     */
    descendantsOf(id: ObjectId): Uint32Array | undefined {
        const entry = this.sets.get(id);
        return entry?.ids === undefined ? undefined : this.expand(id, entry.ids);
    }

    /**
     * Expose `object.descendants` as a lazy accessor over this cache instead of assigning
     * the expanded array. A plain assignment pins the full id list (up to MBs per folder)
     * for the reference's lifetime, and the tree holds thousands of references; the
     * accessor pins nothing, reading through the shrunk set and the bounded hot cache.
     *
     * The getter answers `undefined` for a set that was never stored — a degraded or
     * abandoned read — which is exactly what "the ids are not on the object" means to
     * `warmDescendantIds` and to `treeHelpers`.
     *
     * The setter keeps plain assignments working by demoting the property back to a data
     * property, as `utils/search`' equivalent does.
     */
    defineLazyDescendants(object: HierarcicalObjectReference): void {
        const id = object.id;
        Object.defineProperty(object, "descendants", {
            configurable: true,
            enumerable: true,
            get: () => this.descendantsOf(id) as unknown as ObjectId[] | undefined,
            set(value: ObjectId[] | undefined) {
                Object.defineProperty(this, "descendants", {
                    value,
                    writable: true,
                    configurable: true,
                    enumerable: true,
                });
            },
        });
    }

    /** Retained-structure stats, folded into the on-demand memory report. */
    stats() {
        const entries: { id: ObjectId; path: string; bytes: number; idCount: number }[] = [];
        let inFlight = 0;
        for (const [id, entry] of this.sets) {
            if (entry.pending) {
                inFlight++;
            }
            if (entry.ids !== undefined) {
                entries.push({ id, path: entry.path, bytes: bytesOf(entry.ids), idCount: entry.idCount });
            }
        }
        return {
            entryCount: entries.length,
            /** Resident cost of the shrunk tier. */
            residentBytes: this.storedBytes,
            /** The five biggest sets by stored size, named by path. */
            largestEntries: entries.sort((a, b) => b.bytes - a.bytes).slice(0, 5),
            hotEntryCount: this.hot.size,
            hotIds: this.hotIds,
            hotBytes: this.hotIds * Uint32Array.BYTES_PER_ELEMENT,
            inFlight,
        };
    }

    /**
     * Register an asker against an in-flight read, so the read outlives any one of them
     * giving up and is abandoned once they all have.
     *
     * An asker with no signal is counted and never withdrawn — it cannot give up, so the
     * read cannot be abandoned while it waits.
     */
    private attend(shared: SharedRead | undefined, signal: AbortSignal | undefined): void {
        if (!shared || signal?.aborted) {
            return;
        }
        shared.askers++;
        if (!signal) {
            return;
        }
        const onAbort = () => {
            if (--shared.askers === 0) {
                shared.controller.abort();
            }
        };
        signal.addEventListener("abort", onAbort, { once: true });
        shared.release.push(() => signal.removeEventListener("abort", onAbort));
    }

    /**
     * Register `work` as this id's in-flight read so concurrent askers join it, and clear
     * it again once it settles.
     *
     * An entry left holding no set is dropped rather than kept as a husk — that is the
     * abandoned and the degraded read, neither of which is worth an entry.
     */
    private share(id: ObjectId, entry: Entry, work: Promise<Uint32Array>): Promise<Uint32Array> {
        const pending = work.finally(() => {
            if (entry.pending === pending) {
                entry.pending = undefined;
            }
            // Unsubscribe before the signals outlive the read: a scene's worth of askers
            // would otherwise stay attached to long-lived controllers.
            for (const release of entry.shared?.release ?? []) {
                release();
            }
            entry.shared = undefined;
            if (entry.ids === undefined && this.sets.get(id) === entry) {
                this.sets.delete(id);
            }
        });
        entry.pending = pending;
        this.touch(id, entry);
        return pending;
    }

    private async loadThenKeep(
        id: ObjectId,
        entry: Entry,
        load: (signal: AbortSignal) => Promise<LoadedSet | undefined>,
    ): Promise<Uint32Array> {
        const loaded = await load(entry.shared!.controller.signal);
        if (!loaded) {
            return new Uint32Array();
        }
        if (loaded.complete) {
            this.keep(id, entry, loaded.ids);
        }
        return loaded.ids;
    }

    private keep(id: ObjectId, entry: Entry, ids: Uint32Array): void {
        entry.ids = pack(ids);
        entry.idCount = ids.length;
        this.storedBytes += bytesOf(entry.ids);
        if (entry.ids !== ids) {
            // Seed the hot cache so the caller holding `ids` and the object's accessor
            // observe the same instance instead of expanding again immediately.
            this.putHot(id, ids);
        }

        // Evict oldest-first until within budget, skipping the set just stored. Evicting
        // one mid-read is harmless: a joiner holds the promise itself rather than looking
        // the entry up again, so dropping it costs a later miss and nothing else.
        for (const [oldestId, oldest] of this.sets) {
            if (this.storedBytes <= this.maxTotalBytes) {
                break;
            }
            if (oldestId === id || oldest.ids === undefined) {
                continue;
            }
            this.storedBytes -= bytesOf(oldest.ids);
            this.sets.delete(oldestId);
            this.dropHot(oldestId);
        }
    }

    /** Insert, or refresh LRU position for an entry already here. */
    private touch(id: ObjectId, entry: Entry): void {
        this.sets.delete(id);
        this.sets.set(id, entry);
    }

    private expand(id: ObjectId, stored: CachedDescendants): Uint32Array {
        if (stored instanceof Uint32Array) {
            return stored;
        }
        const cached = this.hot.get(id);
        if (cached) {
            this.hot.delete(id);
            this.hot.set(id, cached);
            return cached;
        }
        const expanded = IdArrayShrinker.expand(stored);
        this.putHot(id, expanded);
        return expanded;
    }

    private putHot(id: ObjectId, expanded: Uint32Array): void {
        this.dropHot(id);
        this.hot.set(id, expanded);
        this.hotIds += expanded.length;

        // Oldest-first, but always keep the entry just inserted — it is last in insertion
        // order, so the size > 1 guard protects it.
        while (this.hotIds > HOT_CACHE_MAX_TOTAL_IDS && this.hot.size > 1) {
            const [oldestId, oldest] = this.hot.entries().next().value!;
            this.hot.delete(oldestId);
            this.hotIds -= oldest.length;
        }
    }

    private dropHot(id: ObjectId): void {
        const previous = this.hot.get(id);
        if (previous) {
            this.hot.delete(id);
            this.hotIds -= previous.length;
        }
    }
}

/** Bytes an entry occupies in the shrunk tier. */
function bytesOf(stored: CachedDescendants): number {
    return stored instanceof Uint32Array ? stored.byteLength : estimateShrunkSize(stored);
}

/**
 * The set in the smaller of its two forms — shrunk, or the ids as they came when
 * shrinking would not pay for itself.
 */
function pack(ids: Uint32Array): CachedDescendants {
    if (ids.length < SHRINK_MIN_IDS) {
        return ids;
    }
    // Pick once: `shrink()` would otherwise re-run every strategy's estimateSize.
    const { type, size } = pickBestShrink(ids);
    return size >= ids.byteLength ? ids : IdArrayShrinker.shrink(ids, type);
}

/**
 * Caches whose contents should show up in memory diagnostics.
 *
 * `WeakRef`s so a cache — and through it a whole scene's sets — is not kept alive by
 * being reportable. Dead refs are pruned on each report.
 */
const liveCaches = new Set<WeakRef<DescendantCache>>();

function trackForDiagnostics(cache: DescendantCache): void {
    liveCaches.add(new WeakRef(cache));
}

registerMemoryStatsProvider("bimIndexDescendantsCache", () => {
    const caches: ReturnType<DescendantCache["stats"]>[] = [];
    for (const ref of liveCaches) {
        const cache = ref.deref();
        if (!cache) {
            liveCaches.delete(ref);
            continue;
        }
        caches.push(cache.stats());
    }
    const total = (pick: (stats: ReturnType<DescendantCache["stats"]>) => number) =>
        caches.reduce((sum, stats) => sum + pick(stats), 0);
    return {
        sessionCount: caches.length,
        entryCount: total((c) => c.entryCount),
        residentBytes: total((c) => c.residentBytes),
        largestEntries: caches
            .flatMap((c) => c.largestEntries)
            .sort((a, b) => b.bytes - a.bytes)
            .slice(0, 5),
        hotEntryCount: total((c) => c.hotEntryCount),
        hotIds: total((c) => c.hotIds),
        hotBytes: total((c) => c.hotBytes),
        inFlight: total((c) => c.inFlight),
    };
});
